// src/pages/DigitalSignature/DigitalSignatureCreate.jsx
// ✅ Corrigido: no modo DOCUMENTO o botão ficava desabilitado porque canSubmit exigia contract_id.
// Agora contract_id é obrigatório só para TEMPLATE.
// Além disso, no FormData só envia contract_id se existir.

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react"
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    TextField,
    Select,
    MenuItem,
    IconButton,
    Button,
    Typography,
    InputLabel,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Tooltip,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Menu,
} from "@mui/material"
import { DeleteOutline, Add, UploadFile, Person, Send } from "@mui/icons-material"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined"
import AddIcon from "@mui/icons-material/Add"
import { useNavigate } from "react-router-dom"

import { api } from "../../../services/Main/Api"
import { getCondoId } from "../../../services/Main/AuthStorage"

const TEMPLATES_ENDPOINT = "sign/template/"
const CONTRACTS_ENDPOINT = "/rent_contract/"
const UNIT_OWNERS_ENDPOINT = "unit_owner/"
const CONDO_ENDPOINT = "condo/"
const DOC_SIGN_CREATE_ENDPOINT = "sign/doc-sign/create/"

const SIGNER_ROLES = [
    { value: "APPROVER", label: "Responsável" },
    { value: "SIGNER", label: "Cliente" },
    { value: "WITNESS", label: "Testemunha" },
]

const roleLabel = (role) => SIGNER_ROLES.find((r) => r.value === role)?.label || role || "-"

const onlyDigits = (v) => String(v || "").replace(/\D/g, "")

const formatCpfCnpj = (value) => {
    const v = onlyDigits(value)
    if (!v) return "-"
    if (v.length <= 11) {
        const cpf = v.padStart(11, "0")
        return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
    }
    const cnpj = v.padStart(14, "0")
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
}

const formatPhoneBR = (value) => {
    const v = onlyDigits(value)
    if (!v) return "-"
    if (v.length === 11) return v.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3")
    if (v.length === 10) return v.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3")
    return v
}

const formatCep = (value) => {
    const v = onlyDigits(value)
    if (!v) return "-"
    if (v.length === 8) return v.replace(/(\d{5})(\d{3})/, "$1-$2")
    return v
}

const joinAddress = ({ street, number, complement, district, city, state, cep }) => {
    const line1 = [street, number].filter(Boolean).join(", ")
    const line2 = [district, city, state].filter(Boolean).join(" - ")
    const line3 = formatCep(cep) !== "-" ? `CEP: ${formatCep(cep)}` : null
    const parts = [line1 || null, complement ? complement : null, line2 || null, line3 || null].filter(Boolean)
    return parts.length ? parts.join(" • ") : "-"
}

const normalizeEmail = (v) => (v || "").trim().toLowerCase()
const signerKey = (s) => normalizeEmail(s?.email) || (s?.name || "").trim().toLowerCase()

const upsertSigner = (list, signer) => {
    const key = signerKey(signer)
    if (!key) return list
    const idx = (list || []).findIndex((x) => signerKey(x) === key)
    if (idx === -1) return [...(list || []), signer]
    const next = [...(list || [])]
    next[idx] = { ...next[idx], ...signer }
    return next
}

export default function DigitalSignatureCreate({ value, onChange, apiClient }) {
    const navigate = useNavigate()
    const fileInputRef = useRef(null)
    const client = api

    const [data, setData] = useState({
        contract_title: "",
        source_type: "template", // "template" | "document"
        template: "",
        contract_id: "",
        document_file: null,
        signers: [],
    })

    const [templates, setTemplates] = useState([])
    const [templatesLoading, setTemplatesLoading] = useState(false)
    const [templatesError, setTemplatesError] = useState("")

    const [contracts, setContracts] = useState([])
    const [contractsLoading, setContractsLoading] = useState(false)
    const [contractsError, setContractsError] = useState("")

    const [contractDetail, setContractDetail] = useState(null)
    const [contractDetailLoading, setContractDetailLoading] = useState(false)
    const [contractDetailError, setContractDetailError] = useState("")

    const [owners, setOwners] = useState([])
    const [ownersLoading, setOwnersLoading] = useState(false)
    const [ownersError, setOwnersError] = useState("")

    const [responsible, setResponsible] = useState(null)
    const [responsibleLoading, setResponsibleLoading] = useState(false)
    const [responsibleError, setResponsibleError] = useState("")

    const [submitLoading, setSubmitLoading] = useState(false)
    const [submitError, setSubmitError] = useState("")
    const [submitSuccess, setSubmitSuccess] = useState("")

    // ✅ Menu papel do proprietário (anchor)
    const [ownerMenuAnchorEl, setOwnerMenuAnchorEl] = useState(null)
    const [ownerMenuRow, setOwnerMenuRow] = useState(null)
    const ownerMenuOpen = Boolean(ownerMenuAnchorEl)

    const openOwnerRoleMenu = (e, ownerRow) => {
        e.preventDefault?.()
        e.stopPropagation?.()
        setOwnerMenuAnchorEl(e.currentTarget)
        setOwnerMenuRow(ownerRow)
    }

    const closeOwnerRoleMenu = () => {
        setOwnerMenuAnchorEl(null)
        setOwnerMenuRow(null)
    }

    const addOwnerWithRole = (role) => {
        const o = ownerMenuRow
        const name = (o?.name || "").trim()
        const email = normalizeEmail(o?.email)

        if (!name || !email || email === "-") {
            closeOwnerRoleMenu()
            return
        }

        setData((prev) => {
            const nextSigners = upsertSigner(prev.signers || [], { name, email, role })
            return { ...prev, signers: nextSigners }
        })

        closeOwnerRoleMenu()
    }

    useEffect(() => {
        closeOwnerRoleMenu()
    }, [data.contract_id])

    const fetchTemplates = useCallback(async () => {
        setTemplatesLoading(true)
        setTemplatesError("")
        try {
            const res = await client.get(TEMPLATES_ENDPOINT, {
                params: { condo_id: getCondoId(), page_size: 200 },
            })
            setTemplates(Array.isArray(res?.data?.results) ? res.data.results : [])
        } catch (err) {
            setTemplates([])
            setTemplatesError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Não foi possível carregar os templates."
            )
        } finally {
            setTemplatesLoading(false)
        }
    }, [client])

    const fetchContracts = useCallback(async () => {
        setContractsLoading(true)
        setContractsError("")
        try {
            const res = await client.get(CONTRACTS_ENDPOINT, {
                params: { unit__condo_id: getCondoId(), page_size: 200 },
            })
            setContracts(Array.isArray(res?.data?.results) ? res.data.results : [])
        } catch (err) {
            setContracts([])
            setContractsError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Não foi possível carregar os contratos."
            )
        } finally {
            setContractsLoading(false)
        }
    }, [client])

    const fetchContractDetail = useCallback(
        async (contractId) => {
            if (!contractId) {
                setContractDetail(null)
                setContractDetailError("")
                setContractDetailLoading(false)
                return null
            }

            setContractDetailLoading(true)
            setContractDetailError("")
            try {
                const res = await client.get(`${CONTRACTS_ENDPOINT}${contractId}/`)
                const detail = res?.data || null
                setContractDetail(detail)
                return detail
            } catch (err) {
                setContractDetail(null)
                setContractDetailError(
                    err?.response?.data?.detail ||
                    err?.response?.data?.message ||
                    "Não foi possível carregar os dados do contrato."
                )
                return null
            } finally {
                setContractDetailLoading(false)
            }
        },
        [client]
    )

    const fetchOwnersByUnit = useCallback(
        async (unitId) => {
            if (!unitId) {
                setOwners([])
                setOwnersError("")
                setOwnersLoading(false)
                return
            }

            setOwnersLoading(true)
            setOwnersError("")
            try {
                const res = await client.get(UNIT_OWNERS_ENDPOINT, { params: { unit_id: unitId } })
                setOwners(Array.isArray(res?.data?.results) ? res.data.results : [])
            } catch (err) {
                setOwners([])
                setOwnersError(
                    err?.response?.data?.detail ||
                    err?.response?.data?.message ||
                    "Não foi possível carregar os proprietários."
                )
            } finally {
                setOwnersLoading(false)
            }
        },
        [client]
    )

    const LEGAL_REPRESENTATIVE_ENDPOINT = "/user/legal-representative/"

    const fetchResponsible = useCallback(async () => {
        const condoId = getCondoId()
        if (!condoId) return

        setResponsibleLoading(true)
        setResponsibleError("")

        try {
            const res = await client.get(LEGAL_REPRESENTATIVE_ENDPOINT, {
                params: { condo_id: condoId },
            })

            const payload = res?.data?.data || null
            const name = (payload?.full_name || payload?.name || "").trim()
            const email = (payload?.email || "").trim().toLowerCase()

            if (name && email) {
                setResponsible({ name, email })
            } else {
                setResponsible(null)
            }
        } catch (err) {
            setResponsible(null)
            setResponsibleError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Não foi possível carregar o responsável legal do condomínio."
            )
        } finally {
            setResponsibleLoading(false)
        }
    }, [client])

    useEffect(() => {
        fetchTemplates()
        fetchContracts()
        fetchResponsible()
    }, [fetchTemplates, fetchContracts, fetchResponsible])

    useEffect(() => {
        if (value) {
            setData({
                contract_title: value.contract_title || value.title || "",
                source_type: value.source_type || "template",
                template: value.template || "",
                contract_id: value.contract_id || value.contract || "",
                document_file: value.document_file || null,
                signers: value.signers || [],
            })
        }
    }, [value])

    useEffect(() => {
        onChange?.(data)
    }, [data, onChange])

    // Só carrega detalhes do contrato/owners quando estiver em TEMPLATE e tiver contrato selecionado
    useEffect(() => {
        let alive = true
        const run = async () => {
            if (data.source_type !== "template") {
                setContractDetail(null)
                setContractDetailError("")
                setContractDetailLoading(false)
                setOwners([])
                setOwnersError("")
                setOwnersLoading(false)
                return
            }

            if (!data.contract_id) {
                setContractDetail(null)
                setContractDetailError("")
                setContractDetailLoading(false)
                setOwners([])
                setOwnersError("")
                setOwnersLoading(false)
                return
            }

            const detail = await fetchContractDetail(data.contract_id)
            const unitId = detail?.unit?.id
            if (!alive) return
            await fetchOwnersByUnit(unitId)
        }

        run()
        return () => {
            alive = false
        }
    }, [data.contract_id, data.source_type, fetchContractDetail, fetchOwnersByUnit])

    const handleTabChange = (_e, next) => {
        const nextType = next === 0 ? "template" : "document"
        setData((prev) => {
            if (nextType === "template") return { ...prev, source_type: "template", document_file: null }
            // quando mudar para documento, não precisa limpar contract_id (pode deixar),
            // mas precisa limpar template pra não confundir validação.
            return { ...prev, source_type: "document", template: "" }
        })
    }
    const tabValue = data.source_type === "template" ? 0 : 1

    const [openSignerModal, setOpenSignerModal] = useState(false)
    const [signerDraft, setSignerDraft] = useState({ name: "", email: "", role: "SIGNER" })

    const canConfirmSigner =
        (signerDraft.name || "").trim().length > 0 && (signerDraft.email || "").trim().length > 0

    const handleAddSignerConfirm = () => {
        const name = (signerDraft.name || "").trim()
        const email = (signerDraft.email || "").trim().toLowerCase()
        const role = signerDraft.role
        if (!name || !email) return

        setData((prev) => ({
            ...prev,
            signers: upsertSigner(prev.signers || [], { name, email, role }),
        }))
        setOpenSignerModal(false)
        setSignerDraft({ name: "", email: "", role: "SIGNER" })
    }

    const handleRemoveSigner = (index) => {
        setData((prev) => ({
            ...prev,
            signers: (prev.signers || []).filter((_, i) => i !== index),
        }))
    }

    const handlePickFile = () => fileInputRef.current?.click()
    const handleFileChange = (e) => {
        const file = e.target.files?.[0] || null
        setData((prev) => ({ ...prev, document_file: file }))
        e.target.value = ""
    }
    const handleRemoveFile = () => setData((prev) => ({ ...prev, document_file: null }))

    const unit = contractDetail?.unit || {}

    const clientInfo = useMemo(() => {
        return {
            id: unit?.id,
            name: unit?.name || contractDetail?.name || "-",
            cpf_cnpj: formatCpfCnpj(unit?.cpf || contractDetail?.cpf_cnpj),
            phone: formatPhoneBR(unit?.phone),
            email: unit?.email || "-",
            address: joinAddress({
                street: unit?.street,
                number: unit?.number,
                complement: unit?.complement,
                district: unit?.district,
                city: unit?.city,
                state: unit?.state,
                cep: unit?.cep,
            }),
            district: unit?.district || "-",
        }
    }, [contractDetail, unit])

    const ownersRows = useMemo(() => {
        return (owners || []).map((o) => ({
            id: o?.id,
            name: o?.name || "-",
            cpf_cnpj: formatCpfCnpj(o?.cpf_cnpj),
            phone: formatPhoneBR(o?.phone),
            email: o?.email || "-",
        }))
    }, [owners])

    const canViewClient = !!data.contract_id && !!clientInfo?.id

    // ✅ sugere título pelo contrato (só no template)
    useEffect(() => {
        if (data.source_type !== "template") return
        if (!data.contract_id) return
        if (contractDetailLoading) return
        if (!contractDetail) return
        if ((data.contract_title || "").trim()) return

        const number = contractDetail?.number || contractDetail?.id
        const name = contractDetail?.name
        const suggested = (name || "").trim() || (number ? `Contrato ${number}` : "")
        if (!suggested) return

        setData((prev) => ({ ...prev, contract_title: suggested }))
    }, [data.contract_id, data.source_type, contractDetailLoading, contractDetail, data.contract_title])

    // ✅ adiciona responsável automaticamente (serve pra template e documento)
    useEffect(() => {
        const respEmail = normalizeEmail(responsible?.email)
        const respName = (responsible?.name || "").trim()
        if (!respEmail || !respName) return

        setData((prev) => {
            const nextSigners = upsertSigner(prev.signers || [], {
                name: respName,
                email: respEmail,
                role: "APPROVER",
            })
            return { ...prev, signers: nextSigners }
        })
    }, [responsible?.email, responsible?.name])

    // ✅ adiciona cliente automaticamente (apenas quando tiver contrato/template)
    useEffect(() => {
        if (data.source_type !== "template") return
        if (!data.contract_id) return
        if (contractDetailLoading) return
        if (!contractDetail) return

        const cliEmail = normalizeEmail(clientInfo?.email)
        const cliName = (clientInfo?.name || "").trim()
        if (!cliEmail || cliEmail === "-" || !cliName) return

        setData((prev) => {
            const nextSigners = upsertSigner(prev.signers || [], {
                name: cliName,
                email: cliEmail,
                role: "SIGNER",
            })
            return { ...prev, signers: nextSigners }
        })
    }, [data.contract_id, data.source_type, contractDetailLoading, contractDetail, clientInfo?.email, clientInfo?.name])

    // ✅ CORREÇÃO AQUI: contrato só é obrigatório no TEMPLATE
    const canSubmit = useMemo(() => {
        const hasSigners =
            (data.signers || []).filter((s) => normalizeEmail(s?.email) && (s?.name || "").trim()).length > 0

        const validSource =
            data.source_type === "template"
                ? !!data.template && !!data.contract_id
                : !!data.document_file

        return hasSigners && validSource && !submitLoading
    }, [data.signers, data.source_type, data.template, data.contract_id, data.document_file, submitLoading])

    const buildPayload = () => {
        const base = {
            condo_id: getCondoId(),
            contract_id: data.contract_id,
            contract_title: (data.contract_title || "").trim() || "",
            source_type: data.source_type,
            signers: (data.signers || []).map((s) => ({
                name: (s.name || "").trim(),
                email: normalizeEmail(s.email),
                role: s.role,
            })),
        }

        if (data.source_type === "template") {
            return { ...base, template_id: data.template }
        }

        const fd = new FormData()
        fd.append("condo_id", String(base.condo_id ?? ""))
        fd.append("contract_title", base.contract_title ?? "")
        fd.append("source_type", base.source_type ?? "")
        fd.append("signers", JSON.stringify(base.signers))

        // ✅ só envia contract_id se tiver (no modo documento ele pode ser vazio)
        if (base.contract_id) {
            fd.append("contract_id", String(base.contract_id))
        }

        // ✅ backend espera request.FILES["file"]
        fd.append("file", data.document_file)
        return fd
    }

    const handleSubmit = async () => {
        setSubmitError("")
        setSubmitSuccess("")

        try {
            setSubmitLoading(true)
            const payload = buildPayload()

            const res = await client.post(DOC_SIGN_CREATE_ENDPOINT, payload)
            const created = res?.data || {}
            const docId = created?.id || created?.pk

            setSubmitSuccess("Documento enviado para assinatura com sucesso!")

            if (docId) {
                navigate(`/assinatura/${docId}`)
            }

            return created
        } catch (err) {
            setSubmitError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Não foi possível enviar para assinatura."
            )
            return null
        } finally {
            setSubmitLoading(false)
        }
    }

    return (
        <Grid container spacing={2} justifyContent={"center"} sx={{ flexGrow: 1, px: "1rem" }}>
            <Grid item xs={10}>
                <Card>
                    <CardHeader
                        title={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="h6">Enviar documento para assinatura</Typography>
                            </Box>
                        }
                        avatar={<Send color={"primary"} />}
                    />
                    <Divider />

                    <CardContent>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                label="Nome do contrato"
                                fullWidth
                                placeholder="Ex.: Contrato de Locação - Apto 302"
                                value={data.contract_title}
                                onChange={(e) => setData((prev) => ({ ...prev, contract_title: e.target.value }))}
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant="fullWidth"
                                sx={{
                                    "& .MuiTab-root": { minHeight: 48 },
                                    "& .MuiTab-root .MuiSvgIcon-root": { opacity: 0.85 },
                                    "& .MuiTab-root.Mui-selected .MuiSvgIcon-root": { opacity: 1 },
                                }}
                            >
                                <Tab label="Modelo" icon={<DescriptionOutlinedIcon />} iconPosition="start" />
                                <Tab label="Documento" icon={<UploadFileOutlinedIcon />} iconPosition="start" />
                            </Tabs>
                        </Box>

                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}>
                                {data.source_type === "template" ? (
                                    <>
                                        <FormControl fullWidth>
                                            <InputLabel>Selecionar modelo</InputLabel>
                                            <Select
                                                label="Selecionar modelo"
                                                value={data.template}
                                                onChange={(e) => setData((prev) => ({ ...prev, template: e.target.value }))}
                                                endAdornment={
                                                    templatesLoading ? (
                                                        <Box sx={{ pr: 2 }}>
                                                            <CircularProgress size={18} />
                                                        </Box>
                                                    ) : null
                                                }
                                            >
                                                {templates.map((t) => (
                                                    <MenuItem key={t.id} value={t.id}>
                                                        {t.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>

                                            {templatesError ? (
                                                <Box mt={1}>
                                                    <Alert
                                                        severity="error"
                                                        action={
                                                            <Button size="small" onClick={fetchTemplates}>
                                                                Recarregar
                                                            </Button>
                                                        }
                                                    >
                                                        {templatesError}
                                                    </Alert>
                                                </Box>
                                            ) : null}
                                        </FormControl>

                                        <Box mt={3}>
                                            <FormControl fullWidth>
                                                <InputLabel>Selecione o contrato</InputLabel>
                                                <Select
                                                    label="Selecione o contrato"
                                                    value={data.contract_id}
                                                    onChange={(e) => setData((prev) => ({ ...prev, contract_id: e.target.value }))}
                                                    endAdornment={
                                                        contractsLoading ? (
                                                            <Box sx={{ pr: 2 }}>
                                                                <CircularProgress size={18} />
                                                            </Box>
                                                        ) : null
                                                    }
                                                >
                                                    {contracts.map((c) => (
                                                        <MenuItem key={c.id} value={c.id}>
                                                            {`${c?.number || c?.id} - ${c?.name || ""}`}
                                                        </MenuItem>
                                                    ))}
                                                </Select>

                                                {contractsError ? (
                                                    <Box mt={1}>
                                                        <Alert
                                                            severity="error"
                                                            action={
                                                                <Button size="small" onClick={fetchContracts}>
                                                                    Recarregar
                                                                </Button>
                                                            }
                                                        >
                                                            {contractsError}
                                                        </Alert>
                                                    </Box>
                                                ) : null}
                                            </FormControl>
                                        </Box>
                                    </>
                                ) : (
                                    <Box sx={{ padding: "2rem 0" }} display="flex" gap={1} alignItems="center" width="100%" flexWrap="wrap">
                                        <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
                                        <Button variant="outlined" startIcon={<UploadFile />} onClick={handlePickFile}>
                                            Selecionar documento
                                        </Button>

                                        {data.document_file ? (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                sx={{
                                                    border: "1px solid rgba(0,0,0,0.12)",
                                                    borderRadius: 1,
                                                    px: 1.5,
                                                    py: 0.8,
                                                }}
                                            >
                                                <Typography variant="body2">{data.document_file.name}</Typography>

                                                <Tooltip title="Remover arquivo">
                                                    <IconButton size="small" color="error" onClick={handleRemoveFile}>
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                Nenhum documento selecionado.
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Detalhes do contrato / proprietários só para TEMPLATE */}
                {data.contract_id && data.source_type === "template" ? (
                    <Box mt={3}>
                        {contractDetailError ? (
                            <Alert
                                severity="error"
                                action={
                                    <Button size="small" onClick={() => fetchContractDetail(data.contract_id)}>
                                        Recarregar
                                    </Button>
                                }
                            >
                                {contractDetailError}
                            </Alert>
                        ) : null}

                        {contractDetailLoading ? (
                            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Carregando dados do contrato...
                                </Typography>
                            </Box>
                        ) : null}

                        {!contractDetailLoading && contractDetail ? (
                            <Card sx={{ borderRadius: 2 }}>
                                <CardHeader
                                    title={
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Box
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: "999px",
                                                    backgroundColor: "#e0235c",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Person fontSize="small" />
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                Cliente
                                            </Typography>
                                        </Box>
                                    }
                                    action={
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                if (!canViewClient) return
                                                navigate(`/alugueis/imoveis/${clientInfo.id}/editar`)
                                            }}
                                            sx={{ color: "#e0235c", fontWeight: 700 }}
                                        >
                                            VER CLIENTE
                                        </Button>
                                    }
                                />
                                <Divider />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Typography sx={{ fontWeight: 700 }}>Nome</Typography>
                                            <Typography sx={{ mb: 1 }}>{clientInfo.name}</Typography>

                                            <Typography sx={{ fontWeight: 700 }}>CPF / CNPJ</Typography>
                                            <Typography sx={{ mb: 1 }}>{clientInfo.cpf_cnpj}</Typography>

                                            <Typography sx={{ fontWeight: 700 }}>E-mail</Typography>
                                            <Typography sx={{ mb: 1 }}>{clientInfo.email}</Typography>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography sx={{ fontWeight: 700 }}>Telefone</Typography>
                                            <Typography sx={{ mb: 1 }}>{clientInfo.phone}</Typography>

                                            <Typography sx={{ fontWeight: 700 }}>Endereço</Typography>
                                            <Typography sx={{ mb: 1 }}>{clientInfo.address}</Typography>

                                            <Typography sx={{ fontWeight: 700 }}>Bairro</Typography>
                                            <Typography sx={{ mb: 1 }}>{clientInfo.district}</Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ) : null}

                        {/* PROPRIETÁRIOS */}
                        <Box mt={3}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                                <CardHeader title={"Proprietários"} sx={{ mt: 3 }} />

                                {ownersLoading ? (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            Carregando proprietários...
                                        </Typography>
                                    </Box>
                                ) : null}
                            </Box>

                            {ownersError ? (
                                <Alert
                                    severity="error"
                                    action={
                                        <Button size="small" onClick={() => fetchOwnersByUnit(contractDetail?.unit?.id)}>
                                            Recarregar
                                        </Button>
                                    }
                                >
                                    {ownersError}
                                </Alert>
                            ) : null}

                            <CardContent>
                                {ownersRows.length > 0 ? (
                                    <>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><b>#</b></TableCell>
                                                        <TableCell><b>Nome</b></TableCell>
                                                        <TableCell><b>CPF / CNPJ</b></TableCell>
                                                        <TableCell><b>Telefone</b></TableCell>
                                                        <TableCell><b>E-mail</b></TableCell>
                                                        <TableCell><b>Ações</b></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {ownersRows.map((o, idx) => {
                                                        const emailOk = !!normalizeEmail(o.email) && o.email !== "-"
                                                        const already = (data.signers || []).some(
                                                            (s) => normalizeEmail(s?.email) === normalizeEmail(o.email)
                                                        )

                                                        return (
                                                            <TableRow key={`${o.id}-${idx}`} hover>
                                                                <TableCell>{idx + 1}</TableCell>
                                                                <TableCell>{o.name}</TableCell>
                                                                <TableCell>{o.cpf_cnpj}</TableCell>
                                                                <TableCell>{o.phone}</TableCell>
                                                                <TableCell>{o.email}</TableCell>
                                                                <TableCell>
                                                                    <Tooltip
                                                                        title={
                                                                            !emailOk
                                                                                ? "Não é possível adicionar: proprietário sem e-mail"
                                                                                : already
                                                                                    ? "Este proprietário já está nos signatários"
                                                                                    : "Escolher papel e adicionar"
                                                                        }
                                                                    >
                                                                        <span>
                                                                            <Button
                                                                                size="small"
                                                                                disabled={!emailOk || already}
                                                                                onClick={(e) => openOwnerRoleMenu(e, o)}
                                                                                startIcon={<AddIcon />}
                                                                            >
                                                                                Signatário
                                                                            </Button>
                                                                        </span>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Menu
                                            anchorEl={ownerMenuAnchorEl}
                                            open={ownerMenuOpen}
                                            onClose={closeOwnerRoleMenu}
                                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                            transformOrigin={{ vertical: "top", horizontal: "left" }}
                                            keepMounted
                                        >
                                            {SIGNER_ROLES.map((r) => (
                                                <MenuItem key={r.value} onClick={() => addOwnerWithRole(r.value)}>
                                                    {r.label}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </>
                                ) : (
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        Nenhum proprietário encontrado para este imóvel.
                                    </Typography>
                                )}
                            </CardContent>
                        </Box>
                    </Box>
                ) : null}

                {/* SIGNATÁRIOS */}
                <CardHeader
                    title={"Signatários"}
                    sx={{ mt: 3 }}
                    action={
                        <Button startIcon={<Add />} onClick={() => setOpenSignerModal(true)}>
                            Adicionar signatário
                        </Button>
                    }
                />
                <Divider />
                <CardContent>
                    {!data.signers || data.signers.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Nenhum signatário adicionado.
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Nome</b></TableCell>
                                        <TableCell><b>Email</b></TableCell>
                                        <TableCell><b>Papel</b></TableCell>
                                        <TableCell align="right"><b>Ações</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.signers.map((s, index) => (
                                        <TableRow key={`${s.email}-${index}`}>
                                            <TableCell>{s.name || "-"}</TableCell>
                                            <TableCell>{s.email || "-"}</TableCell>
                                            <TableCell>{roleLabel(s.role)}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Remover">
                                                    <IconButton color="error" onClick={() => handleRemoveSigner(index)}>
                                                        <DeleteOutline />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Dialog open={openSignerModal} onClose={() => setOpenSignerModal(false)} fullWidth maxWidth="sm">
                        <DialogTitle>Adicionar signatário</DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Nome"
                                        fullWidth
                                        value={signerDraft.name}
                                        onChange={(e) => setSignerDraft((prev) => ({ ...prev, name: e.target.value }))}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        label="Email"
                                        fullWidth
                                        value={signerDraft.email}
                                        onChange={(e) => setSignerDraft((prev) => ({ ...prev, email: e.target.value }))}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Papel</InputLabel>
                                        <Select
                                            label="Papel"
                                            value={signerDraft.role}
                                            onChange={(e) => setSignerDraft((prev) => ({ ...prev, role: e.target.value }))}
                                        >
                                            {SIGNER_ROLES.map((r) => (
                                                <MenuItem key={r.value} value={r.value}>
                                                    {r.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={() => setOpenSignerModal(false)}>Cancelar</Button>
                            <Button variant="contained" onClick={handleAddSignerConfirm} disabled={!canConfirmSigner}>
                                Adicionar
                            </Button>
                        </DialogActions>
                    </Dialog>
                </CardContent>

                <Divider sx={{ mb: 2, mt: 2 }} />

                {submitError ? (
                    <Box sx={{ px: 2, mb: 2 }}>
                        <Alert severity="error">{submitError}</Alert>
                    </Box>
                ) : null}
                {submitSuccess ? (
                    <Box sx={{ px: 2, mb: 2 }}>
                        <Alert severity="success">{submitSuccess}</Alert>
                    </Box>
                ) : null}

                <Box sx={{ display: "flex", justifyContent: "center", pb: 3, pt: "1rem" }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={submitLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <Send />}
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        {submitLoading ? "Enviando..." : "Enviar para assinatura"}
                    </Button>
                </Box>

                {responsibleError ? (
                    <Box sx={{ px: 2, pb: 2 }}>
                        <Alert severity="warning">{responsibleError}</Alert>
                    </Box>
                ) : null}
            </Grid>
        </Grid>
    )
}
